import React, { useMemo, FC } from 'react';

import { Button } from '@material-ui/core';
import { useQuery } from 'react-query';

import publicKeysApi from 'api/rest/publicKeys/publicKeys';
import { ReactComponent as GoogleIcon } from 'assets/icons/general/google.svg';
import QUERY from 'constants/queryKeys';
import { isGoogleLoginTestEnabled } from 'utils/googleLoginTestFlag/googleLoginTestFlag';

type Props = {
  label: string;
};

const GoogleLoginButton: FC<Props> = ({ label }) => {
  const isTestEnabled = isGoogleLoginTestEnabled();

  const { data: publicKeys, isLoading } = useQuery(QUERY.GET_PUBLIC_KEYS, publicKeysApi.get(), {
    staleTime: Infinity,
    enabled: isTestEnabled,
  });

  const href = useMemo(() => {
    if (!isTestEnabled) {
      return undefined;
    }

    const clientId = publicKeys?.googleClientId;
    if (!clientId) {
      return undefined;
    }

    const base = 'https://accounts.google.com/o/oauth2/v2/auth';

    const integrityToken = `mm_${10e15 * Math.random()}`;
    localStorage.setItem('google_integrity_token', integrityToken.toString());

    const url = new URL(base);
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', 'https://app.mojamatura.edu.pl/auth/google_login');
    url.searchParams.set('response_type', 'token');
    url.searchParams.set('state', `{integrity_token=${integrityToken}`);
    url.searchParams.set('scope', 'openid email profile');
    url.searchParams.set('include_granted_scopes', 'true');

    return url.toString();
  }, [isTestEnabled, publicKeys?.googleClientId]);

  if (!isTestEnabled) {
    return null;
  }

  return (
    <Button
      variant='contained'
      component='a'
      href={href}
      size='large'
      disabled={isLoading || !href}
      startIcon={<GoogleIcon width={18} />}
    >
      {label}
    </Button>
  );
};

export default GoogleLoginButton;
